import { forwardRef, ReactNode } from 'react';
import {
  TreeItemCheckbox,
  TreeItemContent,
  TreeItemGroupTransition,
  TreeItemIcon,
  TreeItemIconContainer,
  TreeItemLabel,
  TreeItemProvider,
  TreeItemRoot,
  useTreeItem,
} from '@mui/x-tree-view';
import { TreeNode } from './NavigationTreeView';
import { navTreeContentStyling, navTreeGroupTransition, navTreeLabelStyling } from './styles';

interface Props extends TreeNode {
  depth: number;
  children?: ReactNode;
  includeCheckbox?: boolean;
  isSelected?: (id: string) => boolean;
}

const CustomTreeItem = forwardRef(function CustomTreeItem(
  { label, onSelect, isSelected, id, depth, children, includeCheckbox = false }: Props,
  ref: React.Ref<HTMLLIElement>
) {
  const {
    getRootProps,
    getContentProps,
    getIconContainerProps,
    getLabelProps,
    getGroupTransitionProps,
    status,
  } = useTreeItem({ id, itemId: id, children, label, rootRef: ref });
  const isChildNode = depth > 0;
  return (
    <TreeItemProvider itemId={id} id={id}>
      <TreeItemRoot {...getRootProps()}>
        <TreeItemContent status={status} sx={navTreeContentStyling(depth, status)}>
          <TreeItemIconContainer
            {...getIconContainerProps()}
            onClick={(event) => getContentProps().onClick(event)}
          >
            <TreeItemIcon status={status} />
          </TreeItemIconContainer>
          {includeCheckbox && (
            // @ts-ignore
            (<TreeItemCheckbox
              checked={isSelected && isSelected(id)}
              onClick={() => {
                onSelect && onSelect();
              }}
              visible
            />)
          )}

          <TreeItemLabel
            {...getLabelProps()}
            sx={navTreeLabelStyling(isChildNode)}
            onClick={() => {
              onSelect && onSelect();
            }}
          />
        </TreeItemContent>

        {children && (
          <TreeItemGroupTransition {...getGroupTransitionProps()} sx={navTreeGroupTransition} />
        )}
      </TreeItemRoot>
    </TreeItemProvider>
  );
});

export function NavigationTreeItem({
  label,
  childNodes = [],
  onClick,
  onSelect,
  isSelected,
  id,
  depth,
  includeCheckbox,
}: Props) {
  return (
    <CustomTreeItem
      id={id}
      label={label}
      depth={depth}
      onClick={onClick}
      onSelect={onSelect}
      isSelected={isSelected}
      includeCheckbox={includeCheckbox}
    >
      {childNodes.map((node) => (
        <NavigationTreeItem
          key={node.id}
          depth={depth + 1}
          {...node}
          isSelected={isSelected}
          includeCheckbox={includeCheckbox}
        />
      ))}
    </CustomTreeItem>
  );
}
